import React, { useState, useRef, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { apiClient } from "../../apiclient/apiclient";
import { useNavigate } from "react-router-dom";
import { reconnectSocketWithToken } from "../../socket/socketClient";
import Loader from "../../components/Loader";
export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const logoutTimerRef = useRef(null);

  /* ================= JWT UTILS ================= */

  const parseJwt = (token) => {
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      return JSON.parse(atob(base64));
    } catch {
      return null;
    }
  };

  /* ================= AUTH HELPERS ================= */

  const clearAuth = useCallback(() => {
    localStorage.clear();
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
    navigate("/");
  }, [navigate]);

  const scheduleAutoLogout = useCallback(
    (expiryMs) => {
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);

      const ms = expiryMs - Date.now();
      if (ms <= 0) {
        clearAuth();
        return;
      }

      logoutTimerRef.current = setTimeout(() => {
        toast.info("Session expired. Please log in again.");
        clearAuth();
      }, ms);
    },
    [clearAuth]
  );

  /* ================= AUTO LOGIN (SAME DAY) ================= */

  useEffect(() => {
    const token = localStorage.getItem("token");
    const user = JSON.parse(localStorage.getItem("user"));

    if (!token || !user) return;

    const decoded = parseJwt(token);
    if (!decoded || decoded.exp * 1000 < Date.now()) {
      clearAuth();
      return;
    }

    scheduleAutoLogout(decoded.exp * 1000);

    if (user.role === "delivery-boy" || user.role === "coAdmin") {
  navigate("/agent/agent-dashboard");
} else {
  navigate("/dashboard");
}
  }, [navigate, scheduleAutoLogout, clearAuth]);

  /* ================= VALIDATION ================= */

  const validateEmail = (v) =>
    !v
      ? "Email is required"
      : !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)
      ? "Invalid email"
      : null;

  const validatePassword = (v) =>
    !v ? "Password required" : v.length < 6 ? "Min 6 characters" : null;

  /* ================= LOGIN ================= */

  const handleSubmit = async (e) => {
    e.preventDefault();

    const emailErr = validateEmail(email);
    const passErr = validatePassword(password);

    if (emailErr || passErr) {
      setErrors({ email: emailErr, password: passErr });
      return;
    }

    setErrors({});
    setLoading(true);

    try {
      const { data } = await apiClient.post("/login", { email, password });

      if (!data?.token) {
        toast.error("Login failed");
        return;
      }

      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));

      const decoded = parseJwt(data.token);
      if (decoded?.exp) {
        scheduleAutoLogout(decoded.exp * 1000);
      }

      reconnectSocketWithToken();

      toast.success(`Welcome ${data.user.name}`);

      if (data.user.role === "delivery-boy" || data.user.role === "coAdmin") {
  navigate("/agent/agent-dashboard");
} else {
  navigate("/dashboard");
}

    } catch (err) {
      toast.error("Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  /* ================= UI ================= */

return (
  <div className="min-h-screen flex flex-col md:flex-row">

    {/* ================= MOBILE VIEW ================= */}
   <div className="md:hidden h-screen overflow-hidden flex flex-col items-center justify-center bg-gradient-to-br from-blue-950 via-blue-900 to-blue-950 px-5">

  {/* Logo + Company Name */}
  <div className="text-center mb-6">
    <img
      src="/starnutritionlogo.png"
      alt="Star Nutrition"
      className="w-24 mx-auto mb-3 bg-white p-2 rounded-lg shadow-md"
    />
    <h1 className="text-xl font-semibold text-white tracking-wide">
      Star Nutrition
    </h1>
  </div>

  {/* White Card */}
  <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl p-6">

    <div className="text-center mb-5">
      <h2 className="text-lg font-semibold text-gray-800">
        Welcome Back
      </h2>

      <div className="w-8 h-1 bg-amber-400 mx-auto rounded-full mt-2 mb-3"></div>

      <p className="text-sm text-gray-500">
        Sign in to continue
      </p>
    </div>

    <form onSubmit={handleSubmit} className="space-y-4">

      {/* Email */}
      <div>
        <label className="text-sm font-medium text-gray-700">
          Email Address
        </label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mt-2 w-full h-10 px-4 rounded-lg border border-gray-300
                     focus:outline-none focus:ring-2 focus:ring-amber-400
                     focus:border-amber-400 text-sm"
          placeholder="Enter your email"
        />
      </div>

      {/* Password */}
      <div>
        <label className="text-sm font-medium text-gray-700">
          Password
        </label>
        <div className="relative mt-2">
          <input
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full h-10 px-4 pr-14 rounded-lg border border-gray-300
                       focus:outline-none focus:ring-2 focus:ring-amber-400
                       focus:border-amber-400 text-sm"
            placeholder="Enter your password"
          />
          <button
            type="button"
            onClick={() => setShowPassword((s) => !s)}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-gray-500"
          >
            {showPassword ? "Hide" : "Show"}
          </button>
        </div>
      </div>

     <button
  type="submit"
  disabled={loading}
  className="w-full h-10 bg-blue-900 hover:bg-amber-500
             text-white rounded-lg font-semibold
             transition duration-300 shadow-md
             flex items-center justify-center gap-2
             disabled:opacity-70"
>
  {loading ? <Loader text="signing in....." inline /> : "Sign In"}
</button>

    </form>
  </div>

</div>

    {/* ================= DESKTOP VIEW ================= */}
    <div className="hidden md:flex w-full min-h-screen">

      {/* Left Branding */}
      <div className="w-1/2 bg-gradient-to-br from-blue-900 via-blue-900 to-blue-950 text-white items-center justify-center relative overflow-hidden flex">
        <div className="absolute w-96 h-96 bg-amber-400 opacity-10 rounded-full blur-3xl"></div>

        <div className="relative text-center px-10">
          <img
            src="/starnutritionlogo.png"
            alt="Star Nutrition Logo"
            className="w-28 mx-auto mb-6 bg-white p-3 rounded-xl shadow-lg"
          />
          <h1 className="text-4xl font-bold mb-4 tracking-wide">
            Star Nutrition
          </h1>
          <p className="text-lg opacity-90">
            Smart Inventory Management System
          </p>
        </div>
      </div>

      {/* Right Login */}
      <div className="w-1/2 flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 px-6">

        <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-10">

          <div className="text-center mb-6">
            <h2 className="text-2xl font-semibold text-slate-800">
              Welcome Back
            </h2>

            <div className="w-12 h-1 bg-amber-400 mx-auto rounded-full mt-2 mb-4"></div>

            <p className="text-sm text-slate-500">
              Sign in to continue
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">

            {/* Email */}
            <div>
              <label className="text-sm font-medium text-gray-700">
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 w-full h-11 px-4 rounded-lg border border-gray-300
                           focus:outline-none focus:ring-2 focus:ring-amber-400
                           focus:border-amber-400 text-sm"
                placeholder="Enter your email"
              />
            </div>

            {/* Password */}
            <div>
              <label className="text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="relative mt-2">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full h-11 px-4 pr-14 rounded-lg border border-gray-300
                             focus:outline-none focus:ring-2 focus:ring-amber-400
                             focus:border-amber-400 text-sm"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-gray-500"
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
            </div>

            <button
  type="submit"
  disabled={loading}
  className="w-full h-11 bg-blue-900 hover:bg-amber-500
             text-white rounded-lg font-semibold
             transition duration-300 shadow-md
             flex items-center justify-center gap-2
             disabled:opacity-70"
>
  {loading ? <Loader text="Signing in....." inline /> : "Sign In"}
</button>

          </form>

        </div>
      </div>
    </div>

  </div>
);
}
  